import {
  Address,
  Submission,
  SubmissionDetailData,
  SubmissionInfo,
  SubmissionStatus,
  SubmissionSubStatus,
} from '@converge/graphql';
import {
  ImmerReducer,
  createActionCreators,
  createReducerFunction,
} from 'immer-reducer';
import { createContext, useContext, useReducer } from 'react';

export interface IUnderwritingSubmissionContextState {
  submission: SubmissionDetailData;
  info: SubmissionInfo;
}

export class UnderwritingSubmissionReducer extends ImmerReducer<IUnderwritingSubmissionContextState> {
  updateSubmission(submission: SubmissionDetailData) {
    this.draftState.submission = submission;
  }

  updateInfo(info: SubmissionInfo) {
    this.draftState.info = info;
  }
}

export const UnderwritingSubmissionReducerFunction = createReducerFunction(
  UnderwritingSubmissionReducer
);

export const UnderwritingSubmissionReducerActions = createActionCreators(
  UnderwritingSubmissionReducer
);

export const UnderwritingSubmissionActionsContext = createContext<
  typeof UnderwritingSubmissionReducerActions
>(UnderwritingSubmissionReducerActions);

export const UnderwritingSubmissionDispatchContext =
  createContext<UnderwritingSubmissionReducerDispatch | null>(null);

export const UnderwritingSubmissionStateContext =
  createContext<IUnderwritingSubmissionContextState | null>(null);

export const createUnderwritingSubmissionState = (
  state: IUnderwritingSubmissionContextState
): IUnderwritingSubmissionContextState => ({
  ...state,
});

export const useUnderwritingSubmissionReducer = (
  defaultState: IUnderwritingSubmissionContextState
) => {
  const [state, dispatch] = useReducer(
    UnderwritingSubmissionReducerFunction,
    createUnderwritingSubmissionState(defaultState)
  );

  return [state, dispatch, UnderwritingSubmissionReducerActions] as [
    typeof state,
    typeof dispatch,
    typeof UnderwritingSubmissionReducerActions
  ];
};

export type UnderwritingSubmissionReducerState = ReturnType<
  typeof useUnderwritingSubmissionReducer
>[0];

export type UnderwritingSubmissionReducerDispatch = ReturnType<
  typeof useUnderwritingSubmissionReducer
>[1];

export const useUnderwritingSubmissionDispatchContext = () => {
  const context = useContext(UnderwritingSubmissionDispatchContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }
  return context;
};

export const useUnderwritingSubmissionStateContext = () => {
  const context = useContext(UnderwritingSubmissionStateContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }

  const { submission } = context;

  if (submission) {
    const { status, subStatus } = submission;
    return {
      ...context,
      propertyAndCasualtyLicenseExpired:
        submission?.propertyAndCasualtyLicense?.expired,
      surplusLinesTaxLicenseExpired:
        submission?.surplusLinesTaxLicense?.expired,
      isQuoted:
        !!status &&
        [
          SubmissionStatus.Quoted,
          SubmissionStatus.Bound,
          SubmissionStatus.PolicyIssued,
        ].includes(status),
      isReadOnly:
        status === SubmissionStatus.Cancelled ||
        status === SubmissionStatus.PendingCancellation ||
        ((status !== SubmissionStatus.Evaluation ||
          subStatus !== SubmissionSubStatus.EvaluationEditable) &&
          status !== SubmissionStatus.Referred &&
          status !== SubmissionStatus.Cleared &&
          status !== SubmissionStatus.Indicated &&
          status !== SubmissionStatus.RequestedMoreInfo),
      isScoreAndRateButtonEnabled:
        (status === SubmissionStatus.Evaluation &&
          !!subStatus &&
          subStatus === SubmissionSubStatus.EvaluationEditable) ||
        status === SubmissionStatus.Referred ||
        status === SubmissionStatus.Cleared ||
        status === SubmissionStatus.Indicated ||
        status === SubmissionStatus.RequestedMoreInfo,
      isEditButtonEnabled:
        status &&
        [
          SubmissionStatus.Quoted,
          SubmissionStatus.Evaluation,
          SubmissionStatus.Bound,
        ].includes(status) &&
        subStatus !== SubmissionSubStatus.EvaluationEditable &&
        status !== SubmissionStatus.Referred,
      isSubmissionCleared: status !== SubmissionStatus.Submitted,
    };
  } else {
    return {
      submission: {} as Submission,
      isEditButtonEnabled: false,
      isReadOnly: true,
      isQuoted: false,
      isSubmissionCleared: false,
      isScoreAndRateButtonEnabled: false,
      propertyAndCasualtyLicenseExpired: false,
      surplusLinesTaxLicenseExpired: false,
      info: {
        allowDeclineSubmission: false,
        allowLostSubmission: false,
        isDeclined: false,
        submissionSubStatus: SubmissionSubStatus.DeclinedBor,
        submissionStatus: SubmissionStatus.Bound,
        nextSubmissionStatus: SubmissionStatus.Bound,
        nextSubmissionSubStatus: SubmissionSubStatus.DeclinedBor,
        declinedBecause: '',
        allowReferSubmission: false,
        isReferred: false,
        allowCreatingQuotes: false,
        allowCreateQuotePackage: false,
        quotedQuotes: [],
        hasQuotePackageBeenCreated: false,
        policyTypeName: '',
        insuredEntityName: '',
        allowSelectingQuotesForQuotePackage: false,
        allowBindingQuote: false,
        allowUnbindQuote: false,
        isExcessSubmission: false,
        allowUndoQuotePackage: false,
        submissionStatusIsWrong: false,
        isMissingBoundQuote: false,
        allowSortQuotes: false,
        submission: {
          type: null,
        },
        submissionType: null,
        boundQuote: {
          uuid: '',
          policies: {
            edges: [
              {
                node: {
                  fundamentals: {
                    edges: [
                      {
                        cursor: 0,
                        node: { primary: false },
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
        brokerAddress: {} as Address,
        brokerAgentEmailAddress: '',
        brokerAgentName: '',
        brokerEntityName: '',
        insuredAlias: { name: '' },
        insuredAddress: {} as Address,
        declinationReasons: [
          {
            value: SubmissionSubStatus.DeclinedBor,
            label: '',
            because: '',
          },
        ],
      },
    };
  }
};

export const useUnderwritingSubmissionActionsContext = () => {
  const context = useContext(UnderwritingSubmissionActionsContext);
  if (!context) {
    throw new Error(`Context not found for quote list`);
  }
  return context;
};
